import { useEffect, useRef } from 'react'

import { type ComicReaderPageEntry,readerEntryThumbUrl } from '../../../lib/comicReaderModel'
import type { ComicEpisode } from '../../../lib/comicTypes'
import {
  chromeSelectableMediaCardClass,
  chromeSelectableMediaCornerBadgeClass,
} from '../../ChromeSurface'
import { HorizontalChipStrip } from '../../HorizontalChipStrip'
import { comicPanelRoundSm } from '../comicPanelTokens'

type Props = {
  angular: boolean
  entries: ComicReaderPageEntry[]
  episodesById: Map<string, ComicEpisode>
  pageIndex: number
  onSelectIndex: (index: number) => void
}

/**
 * 阅读器翻页缩略图条：复用可选图卡 + 序号角标（与时间线/候选条一致）。
 */
export function ComicReaderPageStrip({
  angular,
  entries,
  episodesById,
  pageIndex,
  onSelectIndex,
}: Props) {
  const activeRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }, [pageIndex])

  if (entries.length <= 1) return null

  return (
    <HorizontalChipStrip
      className="min-w-0 pb-0.5"
      trackClassName="gap-2"
      role="list"
      aria-label="页面缩略图"
    >
      {entries.map((entry, idx) => {
        const episode = episodesById.get(entry.episodeId)
        const thumb = episode ? readerEntryThumbUrl(episode, entry.page) : null
        const selected = idx === pageIndex
        return (
          <button
            key={entry.key}
            ref={selected ? activeRef : undefined}
            type="button"
            role="listitem"
            aria-current={selected ? 'true' : undefined}
            aria-label={`第 ${idx + 1} 页：${entry.page.title}`}
            className={[
              chromeSelectableMediaCardClass(angular, selected),
              'relative h-14 w-14 shrink-0 sm:h-16 sm:w-16',
              comicPanelRoundSm(angular),
            ].join(' ')}
            onClick={() => onSelectIndex(idx)}
          >
            {thumb ? (
              <img src={thumb} alt="" className="h-full w-full object-cover" draggable={false} />
            ) : (
              <span className="flex h-full w-full items-center justify-center bg-surface-muted text-[10px] font-semibold text-ink-soft">
                {idx + 1}
              </span>
            )}
            <span className={chromeSelectableMediaCornerBadgeClass(angular, 'index')}>
              {idx + 1}
            </span>
          </button>
        )
      })}
    </HorizontalChipStrip>
  )
}
